import React from "react";
import {
  ScanLine,
  Braces,
  Workflow,
  CheckCircle2,
  FileText,
  ArrowRightLeft,
  Code2,
  Mailbox,
  PenLine,
  FileCode,
  Eye,
  Brain,
  Sparkles,
  X,
  Receipt,
  IdCard,
  ClipboardList,
  Building2,
  Truck,
  Scale,
  UserPlus,
  FileSignature,
  ShieldCheck,
  Layers,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { Seo } from "@/components/Seo";
import { FineTuningCallout } from "@/components/FineTuningCallout";

const features = [
  { icon: ScanLine, title: "Automated form capture", desc: "Layout-aware form capture reads faxed, scanned, and photographed forms at any quality — structured, printed, or handwritten." },
  { icon: Braces, title: "Any form, any layout", desc: "Fine-tuned vision-language models drive form processing on invoices, applications, KYC IDs, tax forms, HR onboarding, contracts, shipping, and more." },
  { icon: CheckCircle2, title: "Validation & confidence", desc: "Every captured field ships with a confidence score plus optional business-rule validation and human-in-the-loop review." },
  { icon: Workflow, title: "Structured JSON output", desc: "Clean JSON matched to your target schema — plug it straight into your database, ERP, RCM, CRM, or downstream workflow." },
];

const formTypes = [
  { icon: Receipt, name: "Invoices & receipts" },
  { icon: IdCard, name: "IDs & KYC documents" },
  { icon: ClipboardList, name: "Applications & intake forms" },
  { icon: FileSignature, name: "Contracts & agreements" },
  { icon: UserPlus, name: "HR & onboarding forms" },
  { icon: Building2, name: "Tax & financial forms" },
  { icon: Truck, name: "Shipping & logistics forms" },
  { icon: Scale, name: "Legal & compliance forms" },
];

const capabilities = [
  { icon: Mailbox, title: "Digital mailroom", desc: "Auto-classify and route every form hitting your fax lines, mailroom, and email — end-to-end paper-to-data automation." },
  { icon: PenLine, title: "Handwriting recognition", desc: "AI handwriting recognition captures hand-filled fields on forms that defeat traditional OCR." },
  { icon: Layers, title: "Any file type", desc: "PDFs, scans, faxes, camera photos, TIFFs — multi-page batches supported, extracted in one call." },
  { icon: ShieldCheck, title: "Secure by default", desc: "SOC 2-aligned controls, encryption in transit and at rest, private-cloud and on-premise options for regulated industries." },
];

const devFeatures = [
  { icon: Code2, title: "REST parsing API", desc: "POST any form — an image, PDF, or multi-page scan — and receive structured JSON with per-field confidence in a single response." },
  { icon: FileCode, title: "Schema-first SDK", desc: "Define your target schema once; the SDK maps every form to it consistently, whether it is one page or fifty." },
  { icon: ArrowRightLeft, title: "Webhooks & SFTP", desc: "Push clean data to your systems via webhook, SFTP, or direct database write — no polling, no glue code." },
];

const faqs = [
  {
    q: "What is automated form processing?",
    a: "Automated form processing is the end-to-end workflow of capturing every field from a paper, faxed, scanned, or photographed form and turning it into structured, validated data — without manual keying. Hewto.ai combines automated form capture, agentic document extraction with vision-language models fine-tuned on real forms, per-field validation, and delivery to your target schema — so any form becomes machine-usable data in seconds.",
  },
  {
    q: "What is form capture and how is it different from form processing?",
    a: "Form capture is the step that extracts every field from a form into structured data. Form processing is the broader pipeline that includes form capture plus classification, validation, routing, and delivery to downstream systems. Hewto.ai does both in a single API call — form capture plus full form processing — so an image or PDF becomes validated JSON ready for your database, ERP, RCM, or CRM.",
  },
  {
    q: "Which forms does Hewto.ai's form processing software support?",
    a: "Hewto.ai's form processing software supports any US business or government form — invoices, receipts, KYC IDs, applications, intake forms, contracts, HR onboarding paperwork, tax and financial forms, shipping and logistics documents, and legal/compliance filings. Both printed and handwritten forms are supported, at any scan quality, with the same form capture pipeline.",
  },
  {
    q: "What is Form OCR software?",
    a: "Form OCR software uses AI to read structured, semi-structured, and handwritten forms and convert them into clean, structured data. Hewto.ai's Form OCR API is powered by vision-language models with agentic document extraction — it reads a form the way a trained analyst would, understanding layout, checkboxes, tables, signatures, and stamps, and returns validated JSON matched to your schema.",
  },
  {
    q: "What kinds of forms can the Form OCR API extract?",
    a: "Hewto.ai's Form OCR API handles any US business or government form — invoices, receipts, KYC IDs, applications, intake forms, contracts, HR onboarding paperwork, tax and financial forms, shipping and logistics documents, and legal/compliance filings. Both printed and handwritten forms are supported, at any scan quality.",
  },
  {
    q: "How is Hewto.ai's Form OCR different from traditional OCR?",
    a: "Traditional OCR reads text character-by-character and leaves you with raw strings and coordinates. Hewto.ai uses fine-tuned vision-language models and agentic document extraction — the model sees the whole form, understands its layout, checkboxes, and tables, cross-checks related fields, and returns structured, validated data mapped to your schema instead of raw text.",
  },
  {
    q: "How accurate is the Form OCR API?",
    a: "Hewto.ai delivers 99%+ field-level accuracy on production forms, with per-field confidence scores so low-confidence fields can be flagged for review before they hit your downstream systems. Accuracy holds up on real-world scans, faxes, phone photos, and handwriting where traditional OCR breaks down.",
  },
  {
    q: "Can I define my own output schema?",
    a: "Yes. Define your target JSON schema — field names, types, nested structures, arrays — and Hewto.ai maps every form to it consistently. No brittle regex, no template configuration per form variant. Change the schema and the API adapts.",
  },
  {
    q: "Does the Form OCR API handle handwritten forms?",
    a: "Yes. Hewto.ai's vision-language models are trained on handwritten and hand-annotated forms across industries, so hand-filled fields, signatures, initials, and hand-corrections are captured accurately — with confidence scoring so you know exactly which fields to trust.",
  },
  {
    q: "Can I process multi-page forms and batches?",
    a: "Yes. Send a single form, a multi-page PDF, or a batch of scanned pages in one call — the API classifies each page, groups pages that belong to the same form, and returns structured data per form. High-volume batch processing is supported with horizontal scaling and SLAs.",
  },
  {
    q: "Is it available as an API and SDK for developers?",
    a: "Yes. Developers get a REST parsing API, a schema-first SDK, webhooks, and SFTP intake — plus predictable JSON responses, sandbox credentials, and clear docs so a Form OCR integration can go from zero to production in days, not months.",
  },
  {
    q: "Where does the Form OCR API run — cloud, private cloud, or on-premise?",
    a: "Hewto.ai's Form OCR API is available as a hosted cloud API, a private-cloud deployment inside your VPC, and a fully on-premise install for regulated environments. All deployment options use the same models, schemas, and API contract.",
  },
  {
    q: "Is Hewto.ai's Form OCR HIPAA and SOC 2 compliant?",
    a: "Yes. Hewto.ai operates SOC 2-aligned controls with encryption in transit and at rest, role-based access, and full audit logging. HIPAA-eligible deployments are available with a signed BAA for healthcare-adjacent workloads.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const FormOCR = () => (
  <PageTransition>
    <Seo
      title="Form Processing & Form Capture API | Hewto.ai Form OCR"
      description="Hewto.ai automates form processing and form capture — turn any structured, semi-structured, or handwritten form into clean, validated JSON. Form processing software, form capture API, and agentic form OCR for invoices, KYC IDs, applications, contracts, HR, tax, and more."
      keywords="form processing, form processing software, form processing api, automated form processing, intelligent form processing, ai form processing, form capture, form capture software, form capture api, automated form capture, digital form capture, form data capture, form ocr, form ocr api, form ocr software, document ocr api, ocr form extraction, agentic document extraction, agentic ocr, vision language model ocr, handwritten form ocr, invoice ocr, receipt ocr, kyc ocr, application form ocr, contract ocr, tax form ocr, hr form ocr, shipping document ocr, structured data extraction api, schema first ocr api, digital mailroom software, batch form processing api, pdf to json ocr, extract data from any form, best form ocr api, developer friendly ocr api, on premise ocr api, hipaa ocr api"
      canonical="https://www.hewto.ai/form-ocr"
      jsonLd={faqJsonLd}
    />
    <PageHero
      eyebrow="Products · Form Processing & Capture API"
      title="Automated form processing: turn any form into structured data"
      subtitle="Hewto.ai automates form processing and form capture — agentic document extraction and fine-tuned vision-language models convert scanned, faxed, photographed, or handwritten forms into clean, validated JSON. End-to-end form capture and form processing, mapped to your schema, ready for the systems downstream."
    />

    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {features.map((f, i) => (
          <Reveal key={f.title} delay={(i % 4) * 0.08}>
            <div className="group h-full rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand/10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                <f.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* WHY WE BEAT TRADITIONAL OCR — Agentic VLM */}
    <section className="relative overflow-hidden bg-slate-950 py-24 text-slate-100">
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 10%, rgba(4, 135, 216, 0.35) 0, transparent 45%), radial-gradient(circle at 85% 80%, rgba(4, 135, 216, 0.25) 0, transparent 50%)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/30 bg-brand-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-200">
              <Sparkles className="h-3.5 w-3.5" /> Agentic Document Extraction
            </span>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Why Hewto.ai outperforms traditional form OCR
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-300">
              We don&apos;t use character-by-character OCR. Hewto.ai runs an agentic document
              extraction pipeline built on{" "}
              <span className="text-white">
                vision-language models fine-tuned for structured, semi-structured, and handwritten
                forms
              </span>
              . The model reads a form the way a trained analyst would — looking at the whole page,
              its layout, and every visual cue — so it captures the details a traditional OCR
              engine silently misses.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Reveal>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Eye className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Vision-first, not character-first</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                A VLM sees the form as a whole — box borders, checkboxes, tables, tick-marks,
                signatures, stamps, and overlapping handwriting — instead of stitching guesses
                letter by letter. It reads checkbox crosses and ticks like a human, and
                distinguishes watermark text from value text, label text from value text, and
                background from foreground. Visual context stays intact.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Brain className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Fine-tuned on real forms</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Our models are fine-tuned on real invoices, applications, KYC IDs, contracts, and
                intake forms across industries. They know a totals row is not a line item — so
                extractions map cleanly to fields, not raw text.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Workflow className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Agentic reasoning, per field</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Each field runs through a purpose-built agent that verifies context, cross-checks
                related fields, and self-corrects when something looks off — the kind of judgment a
                deterministic OCR pipeline simply can&apos;t make.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 grid grid-cols-1 gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-2 md:grid-cols-2 md:gap-0">
            <div className="p-8">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-red-300/80">
                <X className="h-4 w-4" /> Traditional OCR misses
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-300">
                {[
                  "Checkbox and tick-mark selections in application headers",
                  "Watermarks and background print bleeding into extracted values",
                  "Label text confused with the value text it describes",
                  "Handwritten signatures, dates, and initials",
                  "Line-item tables misaligned across columns",
                  "Values written outside printed field boundaries",
                  "Multi-page forms where fields span page breaks",
                  "Stamps, watermarks, and hand-corrections",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-brand-500/15 to-brand-500/5 p-8 md:m-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-200">
                <CheckCircle2 className="h-4 w-4" /> Hewto.ai captures
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-100">
                {[
                  "Every checkbox and tick — including partially marked ones",
                  "Crosses, ticks, and checkmarks read the way a human eye reads them",
                  "Watermark text separated from real value text — background noise ignored",
                  "Label text distinguished from the value text it describes",
                  "Handwriting in any box, including signatures and hand-written dates",
                  "Line-item tables with correct row-column alignment",
                  "Out-of-bounds writing correctly assigned to the right field",
                  "Multi-page forms grouped and extracted as one logical record",
                  "Stamps, watermarks, and hand-corrections preserved with confidence scores",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-200" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <FineTuningCallout formName="any form type you handle" />

    {/* FORM TYPES COVERAGE */}
    <section className="bg-slate-50 py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Any form, one API
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Every business form you touch
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              Hewto.ai&apos;s Form OCR API is model-driven, not template-driven. Point it at a new
              form type and it works — no per-form configuration, no fragile regexes, no template
              library to maintain.
            </p>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {formTypes.map((f) => (
                <div
                  key={f.name}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand">
                    <f.icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-medium text-slate-700">{f.name}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-slate-200 bg-slate-950 p-6 shadow-xl">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
              <FileCode className="h-4 w-4 text-brand-200" /> POST /v1/extract
            </div>
            <pre className="mt-4 overflow-x-auto whitespace-pre text-xs leading-relaxed text-slate-200">
{`{
  "form_type": "invoice",
  "vendor": { "name": "Acme Corp", "tax_id": "12-3456789" },
  "invoice_number": "INV-2026-0042",
  "issue_date": "2026-07-14",
  "due_date": "2026-08-14",
  "line_items": [
    { "sku": "AC-140", "qty": 4, "unit_price": 89.00, "total": 356.00 },
    { "sku": "AC-210", "qty": 1, "unit_price": 245.50, "total": 245.50 }
  ],
  "subtotal": 601.50,
  "tax": 48.12,
  "total": 649.62,
  "_confidence": { "invoice_number": 0.99, "total": 0.98 }
}`}
            </pre>
            <p className="mt-4 text-xs text-slate-400">
              Structured JSON output — matched to your target schema, ready for downstream systems.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* CAPABILITIES */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
            Built for production
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Form OCR at real-world scale
          </h2>
        </div>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {capabilities.map((c, i) => (
          <Reveal key={c.title} delay={(i % 4) * 0.08}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
                <c.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* DEVELOPER API & SDK */}
    <section className="bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              For developers
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Form OCR API & schema-first SDK
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              A developer-friendly Form OCR API with predictable JSON, per-field confidence,
              sandbox credentials, and clear docs — from zero to production in days.
            </p>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {devFeatures.map((d, i) => (
            <Reveal key={d.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-white">
                  <d.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{d.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{d.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
      <Reveal>
        <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
          Form OCR FAQ
        </span>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Form data extraction, answered
        </h2>
        <p className="mt-4 leading-relaxed text-slate-600">
          Common questions about Hewto.ai&apos;s Form OCR API — agentic document extraction for any
          business form.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-8" data-testid="form-ocr-faq">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
              <AccordionTrigger
                data-testid={`form-ocr-faq-trigger-${i}`}
                className="text-left text-base font-semibold text-slate-900 hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>

    <CTABand />
  </PageTransition>
);

export default FormOCR;
